import React, { useEffect, useRef } from "react";
import { groupBy, sortBy } from "lodash";
import { COMMENT_OBJECT_TYPE } from "common/constants";
import { useGetCommentsForObjectQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { formatDate } from "../ActivityFeed/dateHelper";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import Loading from "../Common/Icons/Loading";
import { Body } from "../Common/Typography";
import { Wrapper } from "./__styles__/Thread";
import { Comment } from "./Comment";
import { CreateComment } from "./CreateComment";

export const Thread = ({
  objectId,
  objectType,
  canViewComments,
  canCreateComments,
}: {
  objectId: string;
  objectType: COMMENT_OBJECT_TYPE;
  canViewComments: boolean;
  canCreateComments: boolean;
}) => {
  const { data, refetch, loading } = useGetCommentsForObjectQuery({
    skip: !canViewComments,
    variables: {
      objectId,
      objectType,
    },
  });

  const comments = data?.getCommentsForObject || [];
  const sortedComments = sortBy(comments, "createdAt");
  const groupedCommentsByDay = groupBy(sortedComments, comment =>
    formatDate({ dateString: comment.createdAt }),
  );
  const mostRecentCommentId = sortedComments[sortedComments.length - 1]?.id;
  const mostRecentCommentRef = useRef<HTMLDivElement>(null);

  // Scroll to the most recent comment
  useEffect(() => {
    if (!mostRecentCommentId || !mostRecentCommentRef.current) return;

    // Small delay to ensure the comment is fully rendered
    const timeoutId = setTimeout(() => {
      mostRecentCommentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [mostRecentCommentId]);

  return (
    <Wrapper>
      {loading && (
        <FlexRow style={{ justifyContent: "center" }}>
          <Loading size="medium" />
        </FlexRow>
      )}
      {Object.entries(groupedCommentsByDay).map(([day, comments]) => (
        <FlexColumn key={day} style={{ gap: `${spacing.s}` }}>
          <Body size="small" type="emphasis">
            {day}
          </Body>
          <div>
            {comments.map(comment => {
              const isMostRecent = comment.id === mostRecentCommentId;
              return (
                <div
                  key={comment.id}
                  ref={isMostRecent ? mostRecentCommentRef : undefined}
                >
                  <Comment comment={comment} />
                </div>
              );
            })}
          </div>
        </FlexColumn>
      ))}
      {canCreateComments && (
        <CreateComment
          refetchComments={refetch}
          objectId={objectId}
          objectType={objectType}
        />
      )}
    </Wrapper>
  );
};
